import toast from 'react-hot-toast';
import {
  TAX_TYPE_LABELS,
  hugCoursePay,
  type BoxMechanism,
  type HugPercentGroup,
  type PayrollReportRow,
} from '@salaries/shared';

// Export helpers mirroring the Arbox Expenses patterns:
//  - CSV with a UTF-8 BOM so Hebrew opens cleanly in Excel (MovementsReportTab).
//  - Print: open a new window with an HTML table + landscape @page, drive
//    win.print() from the opener (ExpensesPage.exportPrint).
// No PDF/xlsx library — same as expenses.

const STATUS_LABEL: Record<string, string> = {
  draft: 'טרם נשלח',
  sent: 'נשלח',
  approved: 'אושר',
  needs_correction: 'צריך תיקונים',
};
const nis = (n: number) => `₪${Number(n).toLocaleString()}`;
/** Same as nis(), but a negative amount reads as "-₪X" rather than "₪-X". */
const nisSigned = (n: number) => (n < 0 ? `-${nis(-n)}` : nis(n));

/** One exported line. A hugim staff member paid at several percentages gets one
 *  line per percentage (`group`); `first` marks the line that carries the
 *  person-level amounts, so the others don't repeat them. */
type ExportLine = { r: PayrollReportRow; group?: HugPercentGroup; split: boolean; first: boolean };

type ExportColumn = {
  label: string;
  csv: (r: PayrollReportRow) => string | number;
  print: (r: PayrollReportRow) => string;
  /** Per-percentage column: filled on every line, from the line's group. Columns
   *  without it are person-level — on the first line only. */
  line?: (l: ExportLine) => string | number;
};

// `csv` returns a raw value (numbers stay numeric for Excel); `print` returns a
// display string (₪-formatted money). Shared by both CSV + print.
const col = (label: string, get: (r: PayrollReportRow) => number, money = true): ExportColumn => ({
  label,
  csv: get,
  print: (r) => (money ? nis(get(r)) : String(get(r))),
});
const anyNonZero = (rows: PayrollReportRow[], get: (r: PayrollReportRow) => number | undefined) =>
  rows.some((r) => (get(r) ?? 0) !== 0);

/** The export columns for a dataset — the SAME set, in the same order, as the
 *  report table (PayrollReportTab): the union of the box's enabled mechanisms'
 *  columns, then the method-agnostic ones. Optional components (volume bonus,
 *  deduction, withholding, …) appear only when some exported row uses them, so
 *  a box that never uses one keeps a narrow sheet. */
function columnsFor(rows: PayrollReportRow[], mechanisms: BoxMechanism[], hugSplit = false): ExportColumn[] {
  const isGroupClasses = mechanisms.includes('group_classes');
  const isHugim = mechanisms.includes('hugim');
  const isTimeClock = mechanisms.includes('time_clock');
  const withHugGroups = isHugim && hugSplit;
  const cols: ExportColumn[] = [
    { label: 'שם', csv: (r) => r.display_name, print: (r) => r.display_name, line: (l) => l.r.display_name },
  ];
  if (withHugGroups) {
    cols.push({
      label: 'שם החוג',
      csv: () => '',
      print: () => '',
      line: (l) => l.group?.hug_names.join(', ') ?? '',
    });
    if (rows.some((r) => r.hug_groups?.some((g) => g.role === 'secondary'))) {
      cols.push({
        label: 'תפקיד',
        csv: () => '',
        print: () => '',
        line: (l) => (l.group ? (l.group.role === 'secondary' ? 'משני' : 'ראשי') : ''),
      });
    }
  }
  if (rows.some((r) => (r.pnl_unit_id ?? 0) !== 0)) {
    cols.push({
      label: 'יחידת רווח והפסד',
      csv: (r) => r.pnl_unit_name ?? '',
      print: (r) => r.pnl_unit_name ?? '—',
      line: (l) => l.r.pnl_unit_name ?? '',
    });
  }
  if (isHugim) {
    // hug_pay includes the add-ons; shown without them when they have their own
    // column, so the row's columns still add up (same as the table).
    const hasAddons = anyNonZero(rows, (r) => r.addons_pay);
    // The manual additions/offsets always have their own columns (as in the table).
    const hugPay = (r: PayrollReportRow) =>
      hugCoursePay({ ...r, addons_pay: hasAddons ? (r.addons_pay ?? 0) : 0 });
    // Revenue × percent = שכר חוגים, so each line can be checked against the
    // money it was taken from. On a split line the pay is that percentage's own.
    if (withHugGroups) {
      cols.push(
        { label: 'סה"כ הכנסות מחוגים', csv: () => '', print: () => '', line: (l) => l.group?.revenue ?? '' },
        { label: 'אחוז לתשלום', csv: () => '', print: () => '', line: (l) => l.group?.percent ?? '' },
      );
    }
    cols.push({ ...col('שכר חוגים', hugPay), line: (l) => (l.split ? l.group!.pay : hugPay(l.r)) });
    if (hasAddons) cols.push(col('תשלום על נלווים', (r) => r.addons_pay ?? 0));
    cols.push(
      col('תוספות', (r) => r.manual_additions ?? 0),
      col('קיזוזים', (r) => -(r.manual_offsets ?? 0)),
    );
    cols.push({
      ...col('משתתפים', (r) => r.participant_count ?? 0, false),
      line: (l) => (l.split ? l.group!.participant_count : (l.r.participant_count ?? 0)),
    });
  }
  if (isGroupClasses) {
    cols.push(
      col('רשומים', (r) => r.registration_count, false),
      col("צ'ק-אין", (r) => r.checkin_count, false),
      col('ביטולים', (r) => r.late_cancel_count, false),
      col('סך שיעורים', (r) => r.class_count, false),
      col('שכר שיעורים', (r) => r.class_pay),
      col('תוספת סופ"ש', (r) => r.weekend_bonus),
      col('תוספת שיעור', (r) => r.class_bonus),
    );
    if (anyNonZero(rows, (r) => r.volume_bonus)) cols.push(col('בונוס כמות שיעורים', (r) => r.volume_bonus ?? 0));
    // Signed: can be a pure deduction.
    if (anyNonZero(rows, (r) => r.daily_class_adjustment)) {
      cols.push({
        label: 'התאמה יומית',
        csv: (r) => r.daily_class_adjustment ?? 0,
        print: (r) => nisSigned(r.daily_class_adjustment ?? 0),
      });
    }
    cols.push(col('בונוס המרה', (r) => r.conversion_bonus));
    if (anyNonZero(rows, (r) => r.empty_class_pay)) cols.push(col('שיעורים ריקים', (r) => r.empty_class_pay ?? 0));
    if (anyNonZero(rows, (r) => r.cancelled_class_pay)) cols.push(col('שיעורים שבוטלו', (r) => r.cancelled_class_pay ?? 0));
  }
  if (isGroupClasses || isTimeClock) {
    cols.push(
      col(isGroupClasses ? 'סך ימים' : 'ימי נוכחות', (r) => r.workdays_count, false),
      col('שכר בסיס', (r) => r.base_salary),
      col('נסיעות', (r) => r.travel),
    );
  }
  if (isTimeClock) cols.push(col('ימים עם שעות חסרות', (r) => r.incomplete_attendance_days_count ?? 0, false));
  cols.push(col('בונוס חד-פעמי', (r) => r.one_time_bonus));
  // הפחתה is stored positive and exported NEGATIVE: it came off the total, and a
  // bare "300" in a column of amounts reads as money added.
  if (rows.some((r) => (r.deduction ?? 0) > 0)) {
    cols.push(
      {
        label: 'הפחתה',
        csv: (r) => -(r.deduction ?? 0),
        print: (r) => ((r.deduction ?? 0) > 0 ? nisSigned(-(r.deduction ?? 0)) : '—'),
      },
      { label: 'סיבת ההפחתה', csv: (r) => r.deduction_note ?? '', print: (r) => r.deduction_note ?? '—' },
    );
  }
  cols.push(
    {
      label: 'סוג',
      csv: (r) => TAX_TYPE_LABELS[r.tax_type] ?? r.tax_type,
      print: (r) => TAX_TYPE_LABELS[r.tax_type] ?? r.tax_type,
    },
    { label: 'מע"מ', csv: (r) => r.vat, print: (r) => (r.vat > 0 ? nis(r.vat) : '—') },
  );
  const anyWithholding = rows.some((r) => (r.withholding ?? 0) > 0);
  if (anyWithholding) {
    cols.push({
      label: 'ניכוי מס במקור',
      csv: (r) => -(r.withholding ?? 0),
      print: (r) => (r.withholding > 0 ? `-${nis(r.withholding)}` : '—'),
    });
  }
  cols.push(col('סה"כ', (r) => r.total));
  if (anyWithholding) cols.push(col('סה"כ להעברה', (r) => r.net_total ?? r.total));
  cols.push({
    label: 'סטטוס',
    csv: (r) => STATUS_LABEL[r.status] ?? r.status,
    print: (r) => STATUS_LABEL[r.status] ?? r.status,
  });
  return cols;
}

/** One line per row — or, for a hugim staff member paid at several percentages
 *  (or in both roles), one line per percentage. Person-level amounts (bonus,
 *  VAT, total, status, …) sit on the first line only, so summing a column
 *  never counts them twice. */
function exportLines(rows: PayrollReportRow[]): ExportLine[] {
  return rows.flatMap((r): ExportLine[] => {
    const groups = r.hug_groups ?? [];
    if (groups.length <= 1) return [{ r, group: groups[0], split: false, first: true }];
    return groups.map((group, i) => ({ r, group, split: true, first: i === 0 }));
  });
}

export function exportPayrollCsv(rows: PayrollReportRow[], periodLabel: string, mechanisms: BoxMechanism[]): void {
  if (rows.length === 0) return;
  const COLUMNS = columnsFor(rows, mechanisms, true);
  const escape = (v: string | number) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [
    COLUMNS.map((c) => escape(c.label)).join(','),
    ...exportLines(rows).map((l) =>
      COLUMNS.map((c) => escape(c.line ? c.line(l) : l.first ? c.csv(l.r) : '')).join(','),
    ),
  ];
  // Excel-friendly BOM so Hebrew renders correctly.
  const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `salary-${periodLabel.replace(/[^\p{L}\p{N}_-]+/gu, '_')}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function exportPayrollPrint(rows: PayrollReportRow[], periodLabel: string, mechanisms: BoxMechanism[]): void {
  if (rows.length === 0) return;
  const COLUMNS = columnsFor(rows, mechanisms);
  const escapeHtml = (s: string) =>
    String(s).replace(/[&<>]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : '&gt;'));

  const thead = COLUMNS.map((c) => `<th>${escapeHtml(c.label)}</th>`).join('');
  const tbody = rows
    .map((r) => `<tr>${COLUMNS.map((c) => `<td>${escapeHtml(c.print(r))}</td>`).join('')}</tr>`)
    .join('');
  const grandTotal = rows.reduce((s, r) => s + r.total, 0);
  // With ניכוי מס במקור the invoiced total and the transferred amount differ —
  // print both rather than a single ambiguous "סה"כ לתשלום".
  const grandWithholding = rows.reduce((s, r) => s + (r.withholding ?? 0), 0);
  const grandNet = rows.reduce((s, r) => s + (r.net_total ?? r.total), 0);

  const html = `<!DOCTYPE html><html lang="he" dir="rtl"><head><meta charset="UTF-8"><title>דוח שכר</title><style>
    body{font-family:Arial,sans-serif;margin:20px;direction:rtl}
    h1{font-size:20px;margin-bottom:5px}
    .meta{color:#666;font-size:13px;margin-bottom:20px}
    table{width:100%;border-collapse:collapse;font-size:12px;table-layout:auto}
    th{background:#f5f5f5;padding:8px 10px;text-align:right;border-bottom:2px solid #ddd;font-weight:600;white-space:nowrap}
    td{padding:7px 10px;border-bottom:1px solid #eee;vertical-align:top}
    .totals{margin-top:20px;font-size:13px}
    .totals span{font-weight:600}
    @media print{body{margin:0}@page{size:landscape}}
  </style></head><body>
    <h1>דוח שכר — ${escapeHtml(periodLabel)}</h1>
    <div class="meta">${rows.length} אנשי צוות</div>
    <table><thead><tr>${thead}</tr></thead><tbody>${tbody}</tbody></table>
    <div class="totals">סה"כ לתשלום: <span>${nis(grandTotal)}</span></div>
    ${
      grandWithholding > 0
        ? `<div class="totals">ניכוי מס במקור: <span>-${nis(grandWithholding)}</span></div>
    <div class="totals">סה"כ להעברה: <span>${nis(grandNet)}</span></div>`
        : ''
    }
  </body></html>`;

  const win = window.open('', '_blank');
  if (!win) {
    toast.error('חסימת חלונות קופצים מונעת הדפסה');
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  const triggerPrint = () => {
    try {
      win.focus();
      win.print();
    } catch {
      /* user closed the window */
    }
  };
  if (win.document.readyState === 'complete') setTimeout(triggerPrint, 250);
  else win.addEventListener('load', () => setTimeout(triggerPrint, 100));
}
